'use client'

import Image from 'next/image'
import { useCallback, useMemo, useState } from 'react'
import type { FlowAttachment } from '@/types/flow'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { useAttachmentUrl } from '@/hooks/useAttachmentUrl'

interface MessageAttachmentsProps {
  attachments?: FlowAttachment[]
  legacyImageUrl?: string | null
  align?: 'left' | 'right' | 'none'
}

type PreparedAttachment = FlowAttachment & {
  initialUrl: string | null
}

type ActiveAttachmentState = {
  url: string
  caption?: string | null
  name?: string | null
}

type AttachmentPreviewProps = {
  attachment: PreparedAttachment
  onResolvedClick: (payload: {
    attachment: PreparedAttachment
    url: string
  }) => void
}

const AttachmentPreview = ({
  attachment,
  onResolvedClick,
}: AttachmentPreviewProps) => {
  const { url, isFetching, isRefetching, isError, refetch } = useAttachmentUrl({
    s3Key: attachment.s3Key,
    initialUrl: attachment.initialUrl ?? attachment.url ?? undefined,
  })

  const hasDownloadKey =
    typeof attachment.s3Key === 'string' && attachment.s3Key.trim().length > 0
  const displayUrl =
    typeof url === 'string' && url.trim().length > 0 ? url.trim() : null
  const isLoading = isFetching || isRefetching

  const handleClick = useCallback(async () => {
    if (!displayUrl && !hasDownloadKey) {
      return
    }

    let nextUrl = displayUrl

    if (hasDownloadKey) {
      try {
        const result = await refetch()
        if (!result.error && result.data?.url) {
          nextUrl = result.data.url
        }
      } catch {
        // Ignore refetch errors here; the error state is displayed below.
      }
    }

    if (nextUrl) {
      onResolvedClick({
        attachment,
        url: nextUrl,
      })
    }
  }, [attachment, displayUrl, hasDownloadKey, onResolvedClick, refetch])

  const altText =
    attachment.caption ||
    attachment.name ||
    attachment.id ||
    'Flow message image attachment'

  // Check if URL is from S3/DigitalOcean Spaces (presigned URL)
  const isS3Url =
    displayUrl?.includes('digitaloceanspaces.com') ||
    displayUrl?.includes('amazonaws.com')

  return (
    <div className="space-y-1">
      <button
        type="button"
        className="group relative w-full max-w-sm overflow-hidden rounded-lg border border-border bg-background text-left shadow-sm transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-75"
        disabled={isLoading || (!displayUrl && !hasDownloadKey)}
        onClick={handleClick}
      >
        {displayUrl ? (
          <Image
            src={displayUrl}
            alt={altText}
            loading="lazy"
            width={800}
            height={600}
            unoptimized={isS3Url}
            className="h-auto max-h-72 w-full object-cover transition-opacity group-hover:opacity-90"
            sizes="(max-width: 640px) 100vw, 400px"
          />
        ) : (
          <div className="flex h-48 w-full items-center justify-center bg-muted text-sm text-muted-foreground">
            {isLoading
              ? 'Loading attachment...'
              : 'Attachment URL unavailable. Try again later.'}
          </div>
        )}
        {(attachment.caption || attachment.name) && (
          <div className="px-3 py-2 text-xs text-muted-foreground">
            {attachment.caption || attachment.name}
          </div>
        )}
      </button>
      {isError && (
        <p className="text-xs text-destructive">
          Unable to refresh the attachment link. Please try again.
        </p>
      )}
    </div>
  )
}

export function MessageAttachments({
  attachments,
  legacyImageUrl,
  align = 'left',
}: MessageAttachmentsProps) {
  const preparedAttachments = useMemo<PreparedAttachment[]>(() => {
    const normalized: PreparedAttachment[] = []

    if (Array.isArray(attachments)) {
      for (const attachment of attachments) {
        if (!attachment) {
          continue
        }

        const initialUrl =
          (typeof attachment.presignedUrl === 'string' &&
          attachment.presignedUrl.trim().length > 0
            ? attachment.presignedUrl.trim()
            : undefined) ||
          (typeof attachment.url === 'string' &&
          attachment.url.trim().length > 0
            ? attachment.url.trim()
            : undefined) ||
          null

        const hasDownloadKey =
          typeof attachment.s3Key === 'string' &&
          attachment.s3Key.trim().length > 0

        if (!initialUrl && !hasDownloadKey) {
          continue
        }

        normalized.push({
          ...attachment,
          initialUrl,
        })
      }
    }

    if (normalized.length === 0 && legacyImageUrl) {
      normalized.push({
        id: `legacy-${legacyImageUrl}`,
        type: 'image',
        url: legacyImageUrl,
        initialUrl: legacyImageUrl,
      })
    }

    return normalized
  }, [attachments, legacyImageUrl])

  const [activeAttachment, setActiveAttachment] =
    useState<ActiveAttachmentState | null>(null)

  const handleResolvedClick = useCallback(
    (payload: { attachment: PreparedAttachment; url: string }) => {
      setActiveAttachment({
        url: payload.url,
        caption: payload.attachment.caption ?? null,
        name: payload.attachment.name ?? null,
      })
    },
    []
  )

  if (preparedAttachments.length === 0) {
    return null
  }

  const alignmentClass =
    align === 'right'
      ? 'ml-auto text-right'
      : align === 'left'
        ? 'mr-auto text-left'
        : 'text-left'

  return (
    <>
      <div className={`space-y-2 ${alignmentClass}`}>
        {preparedAttachments.map((attachment, index) => (
          <AttachmentPreview
            key={
              attachment.id ?? `${attachment.s3Key ?? 'attachment'}-${index}`
            }
            attachment={attachment}
            onResolvedClick={handleResolvedClick}
          />
        ))}
      </div>

      <Dialog
        open={activeAttachment !== null}
        onOpenChange={isOpen => {
          if (!isOpen) {
            setActiveAttachment(null)
          }
        }}
      >
        <DialogContent className="max-w-3xl">
          {activeAttachment && (
            <div className="flex flex-col space-y-3">
              <Image
                src={activeAttachment.url}
                alt={
                  activeAttachment.caption ||
                  activeAttachment.name ||
                  'Flow message image attachment'
                }
                width={1200}
                height={900}
                unoptimized={
                  activeAttachment.url.includes('digitaloceanspaces.com') ||
                  activeAttachment.url.includes('amazonaws.com')
                }
                className="max-h-[75vh] w-full object-contain"
                sizes="(max-width: 1024px) 100vw, 768px"
              />
              {(activeAttachment.caption || activeAttachment.name) && (
                <p className="text-sm text-muted-foreground">
                  {activeAttachment.caption || activeAttachment.name}
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

export default MessageAttachments
